import React, { useState, useRef } from "react";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";

const siteKey = process.env.REACT_APP_SITE_KEY;

const ContactSection = () => {
  const API_HOST = process.env.REACT_APP_API_URL;
  // State to track form values and errors
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    company: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submissionStatus, setSubmissionStatus] = useState(null);

  const recaptchaRef = useRef(null);

  // Update field value and clear any error for that field
  const handleChange = (event) => {
    const { id, value } = event.target;
    setFormData({ ...formData, [id]: value });
    setFormErrors({ ...formErrors, [id]: "" });
  };

  // Custom validation function
  const validateForm = () => {
    const errors = {};
    // Validate Name (required and minimum 3 characters)
    if (!formData.name || formData.name.trim().length < 3) {
      errors.name = "Name must be at least 3 characters long";
    }
    // Validate Email (required and valid email format)
    if (!formData.email) {
      errors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = "Please enter a valid email address";
      }
    }
    // Validate Mobile (required and minimum 10 characters)
    if (!formData.mobile || formData.mobile.trim().length < 10) {
      errors.mobile = "Mobile number must be at least 10 characters long";
    }
    // Company and Message are optional
    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Run validations
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setSubmissionStatus("error");
      return;
    }

    setSubmissionStatus("submitting");

    try {
      // Execute reCAPTCHA to get token
      const token = await recaptchaRef.current.executeAsync();
      if (!token) {
        setSubmissionStatus("error");
        return;
      }

      // Send form data
      const response = await axios.post(
        `${API_HOST}contact/submit-enquiry/`,
        formData,
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );

      if (response.status === 200 || response.status === 201) {
        // Clear form on successful submission
        setFormData({
          name: "",
          email: "",
          mobile: "",
          company: "",
          message: "",
        });
        setSubmissionStatus("success");
      } else {
        setSubmissionStatus("error");
      }
    } catch (error) {
      setSubmissionStatus("error");
    }
  };

  return (
    <section className="contact pt-110 pb-180">
      <div className="container">
        <div className="row pb-125">
          <div className="section-title pb-60">
            <h3 className="title xb-split-text split-in-right">
              WE CREATE DIGITAL <br /> <span>SOLUTIONS</span>
            </h3>
          </div>
          <ul className="contact-location">
            <li>
              <h3>Location</h3>
              <span>#51, Bhakra Road, Nangal, Punjab-140124</span>
            </li>
            <li className="text-lowercase">
              <h3>Contact</h3>
              <span>
                <a href="mailto:omsoftwareandtechnologies@gmail.com">
                  omsoftwareandtechnologies@gmail.com,
                </a>
                <a href="tel:+916239862469">+91 62398-62469</a>
              </span>
            </li>
            <li>
              <h3>Social</h3>
              <span>
                <a
                  href="https://www.instagram.com/omsoftwares.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Instagram..
                </a>
                <a
                  href="https://www.facebook.com/omsoftwares1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Facebook..
                </a>
                 <a
                  href="https://www.linkedin.com/company/omsoftwares1/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LinkedIn..
                </a>
              </span>
            </li>
          </ul>
        </div>
        <div className="row">
          <div className="section-title contact-title mb-35">
            <span className="sub-title">
              Share your creative ideas with us, and <br /> receive designs that
              captivate and inspire engagement.
            </span>
          </div>
          <div className="contact-form-inner">
            <form className="contact-form pos-rel" onSubmit={handleSubmit}>
              <div className="row">
                {/* Name Field */}
                <div className="col-lg-6">
                  <div className="xb-item--field">
                    <label htmlFor="name">Your Name</label>
                    <input
                      id="name"
                      type="text"
                      placeholder="Mahesh Golatria"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      minLength={3}
                    />
                    {formErrors.name && (
                      <span className="error" style={{ color: "red" }}>
                        {formErrors.name}
                      </span>
                    )}
                  </div>
                </div>

                {/* Email Field */}
                <div className="col-lg-6">
                  <div className="xb-item--field">
                    <label htmlFor="email">Email</label>
                    <input
                      id="email"
                      type="email"
                      placeholder="genz@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                    {formErrors.email && (
                      <span className="error" style={{ color: "red" }}>
                        {formErrors.email}
                      </span>
                    )}
                  </div>
                </div>

                {/* Mobile Field */}
                <div className="col-lg-6">
                  <div className="xb-item--field">
                    <label htmlFor="mobile">Skype/Phone</label>
                    <input
                      id="mobile"
                      type="text"
                      inputMode="numeric"
                      pattern="\d*"
                      placeholder="+91 8123 456 789"
                      value={formData.mobile}
                      onChange={handleChange}
                      required
                      minLength={10}
                    />
                    {formErrors.mobile && (
                      <span className="error" style={{ color: "red" }}>
                        {formErrors.mobile}
                      </span>
                    )}
                  </div>
                </div>

                {/* Company Field (Optional) */}
                <div className="col-lg-6">
                  <div className="xb-item--field">
                    <label htmlFor="company">Company</label>
                    <input
                      id="company"
                      type="text"
                      placeholder="Om Softwares"
                      value={formData.company}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Message Field (Optional) */}
                <div className="col-12">
                  <div className="xb-item--field xb-item--textarea">
                    <label htmlFor="message">Your message</label>
                    <textarea
                      id="message"
                      cols="30"
                      rows="10"
                      placeholder="Hello Jatin, can you help me with..."
                      value={formData.message}
                      onChange={handleChange}
                    ></textarea>
                  </div>
                  {submissionStatus === "submitting" && <p>Submitting...</p>}
                  {submissionStatus === "success" && (
                    <p style={{ color: "green" }}>Message sent successfully!</p>
                  )}
                  {submissionStatus === "error" && (
                    <p style={{ color: "red" }}>
                      Error sending message. Please check the form and try
                      again.
                    </p>
                  )}
                  <button type="submit" className="circle-btn port-btn">
                    <span className="button__bg"></span>Send
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ReCAPTCHA ref={recaptchaRef} sitekey={siteKey} size="invisible" />
    </section>
  );
};

export default ContactSection;
